import React from 'react';
import styled, { keyframes } from 'styled-components';

const bounceIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const SuccessContainer = styled.div<{ $isVisible: boolean }>`
  background: white;
  border-radius: 50%;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: ${props => props.$isVisible ? bounceIn : 'none'} 0.6s ease;
`;

const SuccessIcon = styled.div<{ $variant: 'approve' | 'reject' | 'urgent' }>`
  font-size: 3rem;
  animation: ${pulse} 2s infinite;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.$variant) {
      case 'approve':
        return `
          background: var(--color-green-100);
          color: var(--color-green-600);
        `;
      case 'reject':
        return `
          background: var(--color-red-100);
          color: var(--color-red-600);
        `;
      case 'urgent':
        return `
          background: var(--color-orange-100);
          color: var(--color-orange-600);
        `;
      default:
        return `
          background: var(--color-blue-100);
          color: var(--color-blue-600);
        `;
    }
  }}
`;

const Message = styled.div`
  position: absolute;
  top: 140px;
  text-align: center;
  color: white;
  font-weight: 500;
  font-size: 1.1rem;
  white-space: nowrap;
`;

interface SuccessAnimationProps {
  isVisible: boolean;
  variant: 'approve' | 'reject' | 'urgent';
  message?: string;
  onComplete?: () => void;
}

const ICONS = {
  approve: '✅',
  reject: '❌', 
  urgent: '🚨'
};

const DEFAULT_MESSAGES = {
  approve: 'Story Approved!',
  reject: 'Story Rejected!',
  urgent: 'Marked as Urgent!'
};

export const SuccessAnimation: React.FC<SuccessAnimationProps> = ({
  isVisible,
  variant,
  message,
  onComplete
}) => {
  React.useEffect(() => {
    if (isVisible && onComplete) {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  if (!isVisible) return null;

  return (
    <Overlay $isVisible={isVisible}>
      <div>
        <SuccessContainer $isVisible={isVisible}>
          <SuccessIcon $variant={variant}>
            {ICONS[variant]}
          </SuccessIcon>
        </SuccessContainer>
        <Message>
          {message || DEFAULT_MESSAGES[variant]}
        </Message>
      </div>
    </Overlay>
  );
};